import React, { useEffect, useMemo, useCallback, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Model, useAvailableModels } from '../../../../api/models';
import { useChatStore } from '../../store/chatStore';
import { usePleriStatus } from '../../../onboarding/apis/pleri-status';
import { Image } from '../../../ui/Image';
import { useTranslation } from 'react-i18next';
// Provider logos/icons (memoized)
const ProviderIcon = React.memo(
  ({ provider, size = 24 }: { provider: string; size?: number }) => {
    const iconMap: Record<string, React.ReactNode> = {
      Anthropic: (
        <Image type="provider" name="Anthropic" width={size} height={size} />
      ),
      Amazon: (
        <Image type="provider" name="Amazon" width={size} height={size} />
      ),
      Meta: <Image type="provider" name="Meta" width={size} height={size} />,
      OpenAI: (
        <Image type="provider" name="OpenAI" width={size} height={size} />
      ),
      Cohere: (
        <Image type="provider" name="Cohere" width={size} height={size} />
      ),
      'Mistral AI': (
        <Image type="provider" name="mistral-ai" width={size} height={size} />
      ),
    };

    return (
      iconMap[provider] || (
        <div
          className="flex items-center justify-center rounded-full bg-gradient-to-br from-gray-400 to-gray-500"
          style={{ width: size, height: size }}
        >
          <span className="text-xs font-semibold text-white">
            {provider.substring(0, 1)}
          </span>
        </div>
      )
    );
  },
);

ProviderIcon.displayName = 'ProviderIcon';

interface ModelSelectorProps {
  className?: string;
  condensed?: boolean;
}

export const ModelSelector: React.FC<ModelSelectorProps> = React.memo(
  ({ className = '', condensed: _condensed = false }: ModelSelectorProps) => {
    const { t } = useTranslation('pleri/chat');
    const [isOpen, setIsOpen] = useState(false);
    // Get the selected model ID from global state instead of localStorage
    const selectedModelId = useChatStore((state) => state.selectedModelId);
    const setSelectedModelId = useChatStore(
      (state) => state.setSelectedModelId,
    );

    const { data: pleriStatus, isLoading: isPleriStatusLoading } =
      usePleriStatus();
    const textAreaRef = useChatStore((state) => state.activeInputRef);
    // Fetch available models using React Query
    const { data, isLoading, error, refetch } = useAvailableModels();
    const availableModels = useMemo(
      () =>
        data?.data.filter((model) => {
          if (pleriStatus?.stopPremiumRequests) {
            return !model.isPremium;
          }
          return true;
        }) || [],
      [data?.data, pleriStatus?.stopPremiumRequests],
    );

    // Check if current selected model is still available after filtering (memoized)
    const isSelectedModelAvailable = useMemo(
      () =>
        selectedModelId &&
        availableModels.some((model) => model.id === selectedModelId),
      [selectedModelId, availableModels],
    );

    // Find the currently selected model (memoized)
    const selectedModel = useMemo(
      () =>
        availableModels.find((model: Model) => model.id === selectedModelId),
      [availableModels, selectedModelId],
    );

    // Handle model selection (memoized)
    const handleModelSelection = useCallback(
      (modelId: string) => {
        // Validate that the model exists in available models
        const modelExists = availableModels.some(
          (model) => model.id === modelId,
        );
        if (!modelExists) {
          console.warn(`Model ${modelId} not found in available models`);
          return;
        }

        setSelectedModelId(modelId);
        setIsOpen(false);
        if (textAreaRef?.current) {
          setTimeout(() => {
            textAreaRef.current?.focus();
          }, 300);
        }
      },
      [availableModels, setSelectedModelId, textAreaRef],
    );

    // Group models by provider (memoized)
    const modelsByProvider = useMemo(
      () =>
        availableModels.reduce(
          (acc: Record<string, Model[]>, model: Model) => {
            if (!acc[model.provider]) {
              acc[model.provider] = [];
            }
            acc[model.provider].push(model);
            return acc;
          },
          {} as Record<string, Model[]>,
        ),
      [availableModels],
    );

    // Handle model selection logic - consolidated into a single effect
    useEffect(() => {
      // Don't run if we don't have the necessary data
      if (!pleriStatus || availableModels.length === 0) {
        return;
      }

      const { defaultModelId, defaultNonPremiumModelId, stopPremiumRequests } =
        pleriStatus;

      // Case 1: No model selected - set initial default
      if (!selectedModelId) {
        if (defaultModelId && !stopPremiumRequests) {
          setSelectedModelId(defaultModelId);
        } else if (defaultNonPremiumModelId) {
          setSelectedModelId(defaultNonPremiumModelId);
        }
        return;
      }

      // Case 2: Selected model is no longer available - find replacement
      if (!isSelectedModelAvailable) {
        if (defaultModelId && !stopPremiumRequests) {
          setSelectedModelId(defaultModelId);
        } else if (defaultNonPremiumModelId) {
          setSelectedModelId(defaultNonPremiumModelId);
        } else if (availableModels.length > 0) {
          setSelectedModelId(availableModels[0].id);
        }
        return;
      }

      // Case 3: Premium restrictions active and current model is premium - switch to non-premium
      if (
        stopPremiumRequests &&
        selectedModel?.isPremium &&
        defaultNonPremiumModelId &&
        selectedModelId !== defaultNonPremiumModelId
      ) {
        setSelectedModelId(defaultNonPremiumModelId);
      }
    }, [
      selectedModelId,
      isSelectedModelAvailable,
      selectedModel?.isPremium,
      availableModels,
      pleriStatus,
      setSelectedModelId,
    ]);

    if (isLoading || isPleriStatusLoading) {
      return (
        <div className={`relative ${className}`}>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600"></div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={`relative ${className}`}>
          <div
            onClick={() => refetch()}
            className="flex cursor-pointer items-center justify-center rounded-full bg-gray-100 text-red-500 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-600"
            title={t('clickToRetryLoadingModels')}
          >
            <span className="text-xs">!</span>
          </div>
        </div>
      );
    }

    return (
      <div className={`relative ${className}`}>
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 bg-transparent text-gray-600 transition-colors hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-300"
            >
              {selectedModel?.provider ? (
                <div className="flex-shrink-0">
                  <ProviderIcon provider={selectedModel.provider} size={16} />
                </div>
              ) : (
                <ChevronUpDownIcon
                  className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
              )}
              {selectedModel && (
                <span className="w-20 truncate text-xs text-gray-600 dark:text-gray-400">
                  {selectedModel.name}
                </span>
              )}
              <ChevronUpDownIcon
                className="h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500"
                aria-hidden="true"
              />
            </button>
          </Popover.Trigger>
          <Popover.Content
            side="bottom"
            align="end"
            sideOffset={8}
            avoidCollisions
            collisionPadding={16}
            className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[102] max-h-80 w-72 overflow-auto rounded-xl border border-gray-200 bg-white py-2 text-base shadow-lg focus:outline-none sm:text-sm dark:border-gray-700 dark:bg-gray-900"
          >
            {Object.entries(modelsByProvider).map(([provider, models]) => (
              <div key={provider} className="mb-1 last:mb-0">
                <div className="sticky top-0 z-10 flex items-center gap-2 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  <ProviderIcon provider={provider} />
                  <span>{provider}</span>
                </div>
                <div>
                  {models.map((model: Model) => {
                    const isSelected =
                      selectedModelId &&
                      String(model.id) === String(selectedModelId);
                    return (
                      <button
                        key={model.id}
                        type="button"
                        onClick={() => handleModelSelection(model.id)}
                        className="relative w-full cursor-pointer px-4 py-2 text-left text-sm select-none focus:outline-none"
                      >
                        <div className="flex items-center gap-2">
                          {isSelected && (
                            <CheckIcon
                              className="h-4 w-4 flex-shrink-0 text-[#FF732E]"
                              aria-hidden="true"
                            />
                          )}
                          <div className="flex flex-1 items-center gap-2">
                            <span className="text-gray-700 dark:text-gray-300">
                              {model.name}
                            </span>
                            {model.isPremium && (
                              <span className="inline-flex items-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-1.5 py-0.5 text-xs font-normal text-white">
                                {t('premium')}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </Popover.Content>
        </Popover.Root>
      </div>
    );
  },
);

ModelSelector.displayName = 'ModelSelector';
